<template>
  <div>
    <form-section
      :title="$t('Automations_Title_IntegrationSettings')"
      v-if="model.config.identity === 'otp'"
    >
      <el-row>
        <el-col :span="16">
          <integration-select
            prop="config.integration_id"
            :label="$t('Automations_Field_Integration')"
            :placeholder="$t('Automations_Field_Integration_Select')"
            v-model="integrationId"
          />
        </el-col>
      </el-row>
    </form-section>
    <form-section
      :title="$t('Automations_Title_SegmentSettings')"
      v-if="useSegmentsByIdentity.includes(model.config.identity)"
    >
      <el-row>
        <el-col :span="16">
          <segment-select
            prop="config.segment_id"
            :label="$t('Automations_Field_Segment')"
            :placeholder="$t('Automations_Field_Segment_Select')"
            v-model="segmentId"
            :predefined="predefinedSegments"
          />
        </el-col>
      </el-row>
    </form-section>
    <rest-form-item
      v-if="['fb_ads', 'google_ads'].includes(model.target.type)"
      prop="config.attributes"
    >
      <data-mapper
        :columns="model.config.attributes"
        :translations="translations"
        :translate-samples="true"
        :attributes-prop="attributes"
        :filterable-attributes="filterableAttributes"
        :description="$t('Imports_Title_DataMapping_Export_Description')"
        :placeholder="$t('Imports_Text_DontExport')"
        :sample-column-enabled="model.target.type === 'fb_ads'"
        :fetch-preferences="false"
        :errors="getDataMappingErrors"
      />
    </rest-form-item>
    <el-row v-else-if="useAttributesByIdentity.includes(model.config.identity)">
      <el-col :span="16">
        <attributes-list-draggable
          v-model="model.config.attributes"
          :profile-attributes="model.config.identity === 'profile'"
          :campaign-attributes="model.config.identity === 'campaign'"
          :computed-attributes="isAggregatesEnabled"
        />
        <consent-preferences-list-draggable
          v-model="model.config.preferences"
          v-if="areConsentPreferencesEnabled"
        />
      </el-col>
    </el-row>
    <form-section
      v-else-if="useFieldsByIdentity.includes(model.config.identity)"
      :title="$t('Automations_Title_Fields')"
      :description="$t('Automations_Title_Fields_Description')"
    >
      <el-row>
        <el-col :span="16">
          <export-fields-list-draggable v-model="model.config.fields" />
        </el-col>
      </el-row>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import AttributesListDraggable from '@/modules/automations/components/AttributesListDraggable'
import DataMapper from '@/common/components/DataMapper'
import ConsentPreferencesListDraggable from '@/modules/automations/components/ConsentPreferencesListDraggable'
import SegmentSelect from '@/common/components/segment/SegmentSelect.vue'
import IntegrationSelect from '@/common/components/integration/IntegrationSelect.vue'
import { TranslationsGoogle } from '@/modules/automations/enum/DataStructureScheme'
import { $hasFeature } from '@/core/utils/VueAcl'
import ExportFieldsListDraggable from './ExportFieldsListDraggable'
import RestFormItem from '@/common/components/rest/RestFormItem'

export default {
  name: 'StepExportDataStructure',
  props: {
    model: {
      default: null
    },
    dataStructureErrors: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data() {
    return {
      segments: [],
      attributes: [],
      preferences: [],
      useSegmentsByIdentity: ['profile', 'device', 'consent', 'otp'],
      useAttributesByIdentity: ['profile'],
      useFieldsByIdentity: ['campaign'],
      filterableAttributes: [
        'user_id',
        'email',
        'mobile_number',
        'whatsapp_number',
        'language',
        'timezone',
        'email_addressable',
        'email_subscription',
        'sms_addressable',
        'sms_subscription'
      ],
      dataMappingErrors: []
    }
  },
  components: {
    RestFormItem,
    ExportFieldsListDraggable,
    SegmentSelect,
    DataMapper,
    AttributesListDraggable,
    ConsentPreferencesListDraggable,
    FormSection,
    IntegrationSelect
  },
  computed: {
    translations() {
      switch (this.model.target.type) {
        case 'google_ads':
          return TranslationsGoogle
        default:
          return {}
      }
    },
    segmentId: {
      get: function() {
        return this.model.config.segment_id
      },
      set: function(newValue) {
        this.model.config.segment_id = newValue
      }
    },
    integrationId: {
      get: function() {
        return this.model.config.integration_id
      },
      set: function(newValue) {
        this.model.config.integration_id = newValue
      }
    },
    predefinedSegments() {
      if (this.model.config.identity !== 'consent') return false

      return [
        {
          id: -1,
          title: this.$t('Automations_Segment_AllProfiles')
        }
      ]
    },
    isAggregatesEnabled() {
      return $hasFeature('aggregatesEnabled')
    },
    areConsentPreferencesEnabled() {
      return (
        $hasFeature('subscriptionPreferences') &&
        !['google_ads', 'fb_ads'].includes(this.model.target.type)
      )
    },

    getDataMappingErrors() {
      return [
        ...new Set([...this.dataStructureErrors, ...this.dataMappingErrors])
      ]
    }
  },
  watch: {
    'model.config.attributes': {
      deep: true,
      handler(value) {
        if (['google_ads', 'fb_ads'].includes(this.model.target.type)) {
          if (!value.some(column => column.attribute !== null)) {
            if (
              !this.dataMappingErrors.includes(
                'Automations_Field_Integration_Should_Have_Mapping'
              )
            ) {
              this.dataMappingErrors.push(
                'Automations_Field_Integration_Should_Have_Mapping'
              )
            }
          } else {
            if (
              this.dataMappingErrors.includes(
                'Automations_Field_Integration_Should_Have_Mapping'
              )
            ) {
              let mappingErrorIndex = this.dataMappingErrors.indexOf(
                'Automations_Field_Integration_Should_Have_Mapping'
              )
              this.dataMappingErrors.splice(mappingErrorIndex, 1)
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/automations/components/StepExportDataStructure.vue